/**
 * editor2/hooks/useReservationEditor.ts
 * メインオーケストレーターフック
 * ReservationEditorPanel.tsx の保存・dirty検知・自動変更メモ統合ロジックを抽出
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { generateAutoChangeMemo, formatAutoMemoEntry, getStaffName } from './useAutoChangeMemo';
import type { EditorReservation, StatusOption } from '../types';

interface UseReservationEditorOptions {
  open: boolean;
  editing: EditorReservation | null;
  onChange?: (updates: Partial<EditorReservation>) => void;
  onSave?: (data: EditorReservation, options?: { keepOpen?: boolean }) => Promise<void>;
  statusOptions?: StatusOption[];
}

export default function useReservationEditor({
  open,
  editing,
  onChange,
  onSave,
  statusOptions,
}: UseReservationEditorOptions) {
  const [isSaving, setIsSaving] = useState(false);
  const [originalJson, setOriginalJson] = useState<string | null>(null);

  // 編集中のIDを追跡（リセット用）
  const lastEditingIdRef = useRef<string | null>(null);
  const justSavedRef = useRef(false);

  // 最新のonSave / editing を保持するRef
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const editingRef = useRef(editing);
  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  const isNewReservation = Boolean(editing?.__isNew);
  const isCopyReservation = Boolean(editing?.__isCopy);

  // openがtrueになったタイミングで初期状態を保持
  useEffect(() => {
    if (open && editing) {
      const currentId = editing.id ?? editing.page_id;

      // IDが変わった場合（新規オープンや予約切り替え）
      // または、保存直後（justSavedRefがtrue）の場合はオリジナルを更新してdirty解除
      if (lastEditingIdRef.current !== currentId || justSavedRef.current) {
        setOriginalJson(JSON.stringify(editing));
        lastEditingIdRef.current = currentId;
        justSavedRef.current = false;
      }
    } else if (!open) {
      setOriginalJson(null);
      lastEditingIdRef.current = null;
      justSavedRef.current = false;
    }
  }, [open, editing]);

  // 現在の状態と比較して変更があるか
  const isDirty = useMemo(() => {
    if (!open || !editing || !originalJson) return false;
    // コピー時は初期状態で保存可能とする
    if (isCopyReservation) return true;
    return JSON.stringify(editing) !== originalJson;
  }, [editing, originalJson, open, isCopyReservation]);

  // 保存処理
  const handleSave = useCallback(async () => {
    if (!editing) return;

    // --- 空フィールド検知（データ破損防止） ---
    // 新規・コピー以外で、元々値があったフィールドが空になった場合に警告
    if (!isNewReservation && !isCopyReservation && originalJson) {
      try {
        const original = JSON.parse(originalJson);
        const WATCHED_FIELDS: { key: string; label: string }[] = [
          { key: 'customer_name', label: '予約名' },
          { key: 'car_type', label: '車種' },
          { key: 'vehicle_id', label: '車両' },
          { key: 'pax', label: '乗車人数' },
          { key: 'arrival_flight', label: '到着便' },
          { key: 'departure_flight', label: '出発便' },
          { key: 'contact_media', label: '予約媒体' },
          { key: 'contact_plan', label: 'プラン名' },
          { key: 'base_price', label: '基本料金' },
          { key: 'sum_price', label: '合計金額' },
          { key: 'days', label: '日数' },
          { key: 'nights', label: '泊数' },
          { key: 'comment', label: 'コメント' },
          { key: 'invoice_link', label: 'インボイス' },
          { key: 'customer_email', label: 'メール' },
          { key: 'customer_tel', label: '電話番号' },
        ];

        const isEmpty = (v: any) => v === null || v === undefined || v === '' || v === 0;
        const cleared: string[] = [];

        for (const f of WATCHED_FIELDS) {
          const origVal = original[f.key] ?? original.detail?.[f.key];
          const currVal = (editing as any)[f.key];
          if (!isEmpty(origVal) && isEmpty(currVal)) {
            cleared.push(`${f.label}: ${origVal} → 空`);
          }
        }

        if (cleared.length >= 3) {
          const msg = `⚠️ ${cleared.length}件のフィールドが空になっています:\n\n${cleared.join('\n')}\n\nこのまま保存しますか？`;
          if (!window.confirm(msg)) {
            return; // 保存中止
          }
        }
      } catch {
        // originalJson のパース失敗は無視して保存を続行
      }
    }

    setIsSaving(true);
    try {
      // 自動変更メモを生成して追記
      const autoMemoResult =
        isNewReservation || isCopyReservation
          ? null
          : generateAutoChangeMemo(originalJson, editing, statusOptions);

      if (autoMemoResult) {
        const newEntry = formatAutoMemoEntry(autoMemoResult);
        const currentMemo = editing.memo || '';
        const updatedMemo = currentMemo ? `${currentMemo}\n${newEntry}` : newEntry;

        onChange && onChange({ memo: updatedMemo });

        // 親の state 更新 -> 再レンダー -> onSaveRef 更新を待ってから保存
        // （既存の invoice 保存パターンに準拠: 300ms）
        await new Promise((resolve) => setTimeout(resolve, 300));
        justSavedRef.current = true;
        if (onSaveRef.current) {
          await onSaveRef.current(editingRef.current!, { keepOpen: false });
        }
      } else {
        if (onSave) await onSave(editing, { keepOpen: false });
      }
    } finally {
      setIsSaving(false);
    }
  }, [editing, isNewReservation, isCopyReservation, originalJson, statusOptions, onChange, onSave]);

  // キャンセル処理（originalJsonリセットなし、親に委譲）
  const handleCancel = useCallback(() => {
    // 親コンポーネントの onCancel を呼ぶのは呼び出し側の責務
  }, []);

  // 保存後にoriginalJsonを手動更新する関数（invoice発行等で使用）
  const markSaved = useCallback(() => {
    justSavedRef.current = true;
  }, []);

  return {
    isSaving,
    isDirty,
    isNewReservation,
    isCopyReservation,
    originalJson,
    handleSave,
    handleCancel,
    markSaved,
    onSaveRef,
    editingRef,
    justSavedRef,
  };
}
